import { useState, useEffect } from 'react';
import { TEMP_MAX, TEMP_MIN } from '../constants.js';
import { notify } from '../lib/notify.js';
import { dayHeading, dayKey, formatTimePart, parseEnteredTime, toLocalDatetimeValue } from '../lib/format.js';
import { getDeviceName } from '../lib/device.js';
import { Save, Trash2 } from '../components/icons.jsx';
import { PickChildHint } from '../components/PickChildHint.jsx';
import { TemperatureChart } from '../components/TemperatureChart.jsx';

export function TemperatureView({darkMode, childrenList, temperatureLog, dosageHistory, addTemperature, deleteTemperature, restoreTemperature, defaultChildId}) {
  // The child is chosen once, in the banner at the top of the app.
  const childId = defaultChildId || '';
  const [tempC, setTempC] = useState('');
  const [when, setWhen] = useState(() => toLocalDatetimeValue(new Date()));
  // <input type="datetime-local"> only carries minutes, so parsing its value
  // back zeroes the seconds: two readings logged inside the same minute got
  // byte-identical timestamps, and then sorted arbitrarily against each other in
  // Records and stacked on one x-position in the chart. Until the field is
  // actually edited it just means "now", so stamp it at full precision and only
  // honour the field once the user has set a time themselves.
  const [whenEdited, setWhenEdited] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  // "Saved for Emma" shouldn't linger once the banner switches to another child.
  useEffect(() => { setConfirmation(''); }, [childId]);
  const [isSaving, setIsSaving] = useState(false);

  const resetTimeToNow = () => { setWhen(toLocalDatetimeValue(new Date())); setWhenEdited(false); };

  // While untouched the field means "now", so keep it showing now. It was only
  // refreshed on mount and after a log, so it sat displaying the time of the
  // previous reading — log again ten minutes later without touching it and the
  // form claimed the earlier time (and, before the stamping fix, saved it).
  useEffect(() => {
    if (whenEdited) return;
    const id = setInterval(() => setWhen(toLocalDatetimeValue(new Date())), 30000);
    return () => clearInterval(id);
  }, [whenEdited]);

  const log = async () => {
    const t = parseFloat(tempC);
    if (!childId) { notify('Choose a child at the top of the screen first'); return; }
    if (!t || t < TEMP_MIN || t > TEMP_MAX) {
      notify(`Enter a temperature between ${TEMP_MIN} and ${TEMP_MAX} °C`);
      return;
    }
    const entered = whenEdited ? parseEnteredTime(when) : null;
    if (whenEdited && !entered) return;
    const child = childrenList.find(c => c.id === childId);
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 200));
    const entry = {
      id: crypto.randomUUID(),
      timestamp: (entered || new Date()).toISOString(),
      tempC: t,
      childId,
      childName: child?.name,
      // Same stamp doses carry: with two phones logging, "who took this
      // reading?" is as fair a question of a temperature as of a dose.
      // Readings taken before this existed simply have none.
      loggedBy: getDeviceName() || undefined,
    };
    addTemperature(entry);
    setConfirmation(`${t.toFixed(1)} °C recorded for ${child?.name || 'child'}`);
    setTempC('');
    resetTimeToNow();
    setTimeout(() => setIsSaving(false), 300);
  };

  // Recent readings for the selected child (or all if none selected), newest
  // first — sorted here rather than assumed, since the day headings depend on it.
  const readings = (childId ? temperatureLog.filter(e => e.childId === childId) : [...temperatureLog])
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  // Timeline data for the selected child: their temps + their logged doses.
  const chartTemps = childId ? temperatureLog.filter(e => e.childId === childId) : [];
  const chartMeds = childId ? dosageHistory.filter(e => e.childId === childId) : [];

  const inputCls = `w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
    darkMode ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  return (
    <div className={`rounded-2xl shadow-xl p-4 sm:p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
      {confirmation && (
        <div className={`border rounded-lg p-4 mb-6 ${darkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200'}`}>
          <p className={`font-semibold text-center ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
            ✓ {confirmation}
          </p>
        </div>
      )}

      {childrenList.length === 0 ? (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-50 text-yellow-800'}`}>
          <p className="text-sm">Add a child profile in Settings first.</p>
        </div>
      ) : (
        <div className="space-y-4 mb-6">
          {!childId && <PickChildHint darkMode={darkMode} />}

          {/* The reading and Record share a row, like the Calculator's
              Concentration + Calculate: a reading is a few characters, and
              the button sits right where the thumb already is. */}
          <div>
            <label htmlFor="tempC" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Temperature (°C) *
            </label>
            {/* Two equal halves: a grid, since equal flex shares still differ
                by the button's extra padding. */}
            <div className="grid grid-cols-2 gap-2">
              <input id="tempC" type="number" step="0.1" min={TEMP_MIN} max={TEMP_MAX} inputMode="decimal"
                value={tempC} onChange={(e) => { setTempC(e.target.value); setConfirmation(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !isSaving) log(); }}
                placeholder="e.g., 38.5"
                className={`min-w-0 ${inputCls}`} />
              <button onClick={log}
                disabled={isSaving || !childId || !(parseFloat(tempC) > 0)}
                className={`min-w-0 text-white font-semibold px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
                  !childId || !(parseFloat(tempC) > 0)
                    ? 'bg-gray-500 cursor-not-allowed opacity-50'
                    : isSaving
                    ? 'bg-indigo-700 scale-95'
                    : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
                }`}>
                <Save className={`w-4 h-4 flex-shrink-0 ${isSaving ? 'animate-pulse' : ''}`} />
                {isSaving ? 'Recording...' : 'Record'}
              </button>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Date &amp; Time
            </label>
            <div className="flex gap-2">
              <input type="datetime-local" value={when} onChange={(e) => { setWhen(e.target.value); setWhenEdited(true); }}
                className={`flex-1 min-w-0 ${inputCls}`} />
              <button type="button" onClick={resetTimeToNow}
                className={`flex-shrink-0 px-4 py-2 sm:py-3 rounded-lg font-medium transition ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}>
                Now
              </button>
            </div>
          </div>
        </div>
      )}

      {childId && (chartTemps.length > 0 || chartMeds.length > 0) && (
        <div className="mb-6">
          <TemperatureChart key={childId} darkMode={darkMode} temps={chartTemps} meds={chartMeds} />
        </div>
      )}

      {readings.length > 0 && (
        <div>
          <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Recent Readings
          </h3>
          <div className="space-y-2">
            {/* Grouped under day headings like Records, so each reading only
                needs its time. */}
            {readings.slice(0, 20).map((entry, i, shown) => {
              const newDay = i === 0 || dayKey(shown[i - 1].timestamp) !== dayKey(entry.timestamp);
              const fever = entry.tempC >= 39 ? 'high' : entry.tempC >= 38 ? 'mild' : 'none';
              const tempColor = fever === 'high'
                ? 'text-red-500'
                : fever === 'mild'
                ? 'text-orange-500'
                : darkMode ? 'text-green-400' : 'text-green-700';
              return [
                newDay && (
                  <h4 key={`day-${dayKey(entry.timestamp)}`}
                    className={`text-xs font-semibold uppercase tracking-wide px-1 ${i === 0 ? '' : 'pt-3'} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {dayHeading(entry.timestamp)}
                  </h4>
                ),
                <div key={entry.id} className={`rounded-lg p-3 border flex justify-between items-center ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div>
                    {/* Same as Records: the colour already says "fever". */}
                    <p className={`text-lg font-bold ${tempColor}`}>
                      {Number(entry.tempC).toFixed(1)} °C
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {entry.childName ? `${entry.childName} • ` : ''}{formatTimePart(entry.timestamp)}
                    </p>
                  </div>
                  {/* Deleted at once with Undo, as in Records — it used to
                      delete with no confirmation and no way back. */}
                  <button aria-label="Delete reading"
                    onClick={() => {
                      deleteTemperature(entry.id);
                      notify(`Deleted ${Number(entry.tempC).toFixed(1)} °C reading`, {
                        tone: 'info',
                        action: { label: 'Undo', onClick: () => restoreTemperature(entry) },
                      });
                    }}
                    className={`p-2 rounded-lg transition ${
                      darkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-200 text-red-600'
                    }`}>
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                  </button>
                </div>
              ];
            })}
          </div>
        </div>
      )}
    </div>
  );
}
